import dayjs from 'dayjs';
import { array, boolean, date, InferType, object, string } from 'yup';

import { fieldValidations } from '@zeta-core';
import { ICaptureFactFindPersonalDetailsRequest } from '@zeta-types';

const validationSchema = object().shape({
  id: string(),
  title: string().nullable(),
  firstname: string(),
  middlename: string(),
  lastname: string(),
  dob: date().typeError('Please enter valid date of birth').nullable(),
  age: string(),
  email: string().email('Please enter valid email address').typeError('Please enter valid email address'),
  gender: string().typeError('Please select Gender').nullable(),
  nationality: string().nullable(),
  isUSCitizen: string().nullable(),
  citizenship: string(),
  countryOfBirth: string().typeError('Please select valid country code').nullable(),
  placeOfBirth: string(),
  niNumber: string(),
  marital: string().nullable(),
  phone: string(),
  mobile: string(),
  dateOfDeath: string().nullable(),

  employmentDetails: array().of(
    object().shape({
      id: string(),
      jobTitle: string(),
      employerName: string(),
      employmentStatus: string().nullable(),
      employmentType: string().nullable(),
      jobDescription: string(),
      employmentStartDate: string().nullable(),
      employmentEndDate: string().nullable(),
      department: string(),
      workLocation: string(),
    })
  ),

  partnerID: string(),
  clientID: string(),

  taxResidency: array().of(
    object().shape({
      id: string(),
      country: string().nullable(),
      tin: string(),
      residencyType: string().nullable(),
      isPrimary: string().nullable(),
    })
  ),

  addresses: array().of(
    object().shape({
      id: string(),
      addressType: string(),
      addressLine1: string(),
      addressLine2: string(),
      city: string(),
      country: string(),
      postcode: string(),
      sameAsClient: boolean(),
    })
  ),
});

const dependentsSchema = array().of(
  object().shape({
    id: string(),
    fullName: string(),
    age: string(),
    gender: string().typeError('Please select Gender').nullable(),
    startDate: string().nullable(),
    endDate: string().nullable(),
    cost: string(),
    dateOfBirth: date().typeError('Please enter valid date of birth').nullable(),
    schoolName: string(),
  })
);

export const addPartnerSchema = object().shape({
  title: string().required('Title is required'),
  firstName: fieldValidations.firstName,
  middleName: string(),
  lastName: fieldValidations.lastName,
  email: fieldValidations.email,
  countryCode: fieldValidations.countryCode,
  mobile: fieldValidations.mobileNumber,
});

export const personalInformationValidationSchema = object().shape({
  client: validationSchema,
  partner: validationSchema,
  dependents: dependentsSchema,
});

export const defaultValues = {
  id: '',
  title: '',
  firstname: '',
  middlename: '',
  lastname: '',
  dob: undefined,
  age: '',
  email: '',
  nationality: '',
  tin: '',
  niNumber: '',
  occupation: '',
  employerName: '',
  marital: '',
  phone: '',
  mobile: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  country: '',
  postcode: '',
  taxResidency: [
    {
      country: '',
      tin: '',
      residencyType: '',
      isPrimary: '',
    },
  ],
  employmentDetails: [
    {
      id: '',
      jobTitle: '',
      employerName: '',
      employmentStatus: '',
      employmentType: '',
      jobDescription: '',
      employmentStartDate: '',
      employmentEndDate: '',
      department: '',
      workLocation: '',
    },
  ],
  addresses: [
    {
      addressType: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      country: '',
      postcode: '',
      sameAsClient: false,
    },
  ],
};

export const personalInformationDefaultValues = {
  client: defaultValues,
  partner: defaultValues,
  dependents: [defaultValues],
};

export const addPartnerDefaultValues = {
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  countryCode: { code: '', label: '', phone: '' },
  mobile: '',
};

type SchemaType = InferType<typeof personalInformationValidationSchema>;

type validationSchemaType = InferType<typeof validationSchema>;

const getDependentCostvalue = (dependent: any) => {
  if (dependent.cost && dependent.fullName) return dependent.cost;
  else if (!dependent.cost && dependent.fullName) return 0;
  else return '';
};

const formatDate = (date: any) => (date !== undefined ? (dayjs(date).isValid() ? dayjs(date).format('YYYY-MM-DD') : '') : undefined);

const mapTaxResidency = (taxResidency: any[] = []) =>
  taxResidency.map((tax) => ({
    id: tax.id,
    country: tax.country,
    tin: tax.tin,
    taxResidencyType: tax.residencyType,
    isPrimary: tax.isPrimary ? tax?.isPrimary?.toLowerCase() === 'yes' : null,
  }));

const mapEmploymentDetails = (employmentDetails: any[] = []) =>
  employmentDetails.map((employment) => ({
    id: employment.id,
    jobTitle: employment.jobTitle || '',
    employerName: employment.employerName || '',
    employmentStatus: employment.employmentStatus || '',
    employmentType: employment.employmentType || '',
    jobDescription: employment.jobDescription || '',
    startDate: formatDate(employment.employmentStartDate),
    endDate: formatDate(employment.employmentEndDate),
    department: employment.department,
    workLocation: employment.workLocation,
  }));

const mapAddresses = (addresses: any[] = []) =>
  addresses.map((address) => ({
    id: address.id,
    addressType: address.addressType,
    addressLine1: address.addressLine1,
    addressLine2: address.addressLine2,
    city: address.city,
    country: address.country,
    postcode: address.postcode,
  }));

const mapDetails = (details: validationSchemaType) => ({
  id: details.id,
  title: details.title,
  firstname: details.firstname,
  middlename: details.middlename,
  lastname: details.lastname,
  dob: details.dob,
  age: details.age,
  email: details.email,
  nationality: details.nationality,
  niNumber: details.niNumber,
  marital: details.marital,
  phone: details.phone,
  mobile: details.mobile,
  dateOfDeath: details.dateOfDeath,
  isUsCitizen: details.isUSCitizen,
  gender: details.gender,
  citizenship: details.citizenship,
  countryOfBirth: details.countryOfBirth,
  placeOfBirth: details.placeOfBirth,
  taxResidency: mapTaxResidency(details.taxResidency),
  employmentDetails: mapEmploymentDetails(details.employmentDetails),
  addresses: mapAddresses(details.addresses),
});

const mapPersonalDetails = (payload: any) => {
  return {
    title: payload?.title,
    firstName: payload?.firstname,
    middleName: payload?.middlename,
    lastName: payload?.lastname,
    dateOfBirth: formatDate(payload?.dob),
    dateOfDeath: formatDate(payload?.dateOfDeath),
    email: payload?.email,
    nationality: payload?.nationality,
    niNumber: payload?.niNumber,
    martialStatus: payload?.marital,
    telephoneNumber: payload?.phone,
    mobileNumber: payload?.mobile,
    countryOfBirth: payload?.countryOfBirth,
    isUsCitizen: payload?.isUsCitizen ? payload?.isUsCitizen?.toLowerCase() === 'yes' : undefined,
    gender: payload?.gender,
    citizenship: payload?.citizenship,
    placeOfBirth: payload?.placeOfBirth,
  };
};

const mapDependent = (dependent: any) => {
  return {
    id: dependent?.id || '',
    firstName:
      (dependent?.fullName &&
        dependent?.fullName
          ?.split(' ')
          ?.splice(0, dependent?.fullName?.split(' ')?.length - 1)
          ?.join(' ')) ||
      dependent?.fullName?.split(' ')[0],
    lastName:
      dependent?.fullName && dependent?.fullName?.split(' ')?.length > 1 ? dependent?.fullName?.split(' ')[dependent?.fullName?.split(' ')?.length - 1] : '',
    dateOfBirth: formatDate(dependent.dateOfBirth),
    gender: dependent.gender,
    age: dependent.dateOfBirth ? dependent.age : '',
    schoolName: dependent.schoolName,
    costOfSchoolAmount: getDependentCostvalue(dependent),
    costOfSchoolCurrency: getDependentCostvalue(dependent) ? 'GBP' : '',
    startDate: formatDate(dependent.startDate),
    endDate: formatDate(dependent.endDate),
  };
};

export const createPayload = (values: SchemaType, cleanData: boolean): ICaptureFactFindPersonalDetailsRequest => {
  const clientPayload = cleanData ? cleanObject(mapDetails(values.client)) : mapDetails(values.client);
  const partnerhasData = cleanObject(mapDetails(values.partner));
  const partnerDetails = partnerhasData ? mapDetails(values.partner) : null;
  const partnerPayload = cleanData ? cleanObject(mapDetails(values.partner)) : partnerDetails;
  const dependentPayload = values?.dependents?.map((dependent) => mapDependent(cleanObject(dependent)));

  const finalObject = {
    clientDetails: {
      personalPiiDetails: mapPersonalDetails(clientPayload),
      taxDetails: clientPayload.taxResidency,
      employmentDetails: clientPayload.employmentDetails,
      addresses: clientPayload.addresses,
    },
    partnerDetails: {
      personalPiiDetails: mapPersonalDetails(partnerPayload),
      taxDetails: partnerPayload?.taxResidency,
      employmentDetails: partnerPayload?.employmentDetails,
      addresses: partnerPayload?.addresses,
    },
    dependentDetails: dependentPayload,
  };

  console.log(finalObject);
  // golang api expects clean data
  if (cleanData) {
    return cleanObject(finalObject);
  }
  // data team api expects data as it is
  return finalObject;
};

function cleanObject(obj: any) {
  // Check if the value is an object or array that can be traversed
  const isObject = (val: any) => val && typeof val === 'object' && !Array.isArray(val);
  const isArray = (val: any) => Array.isArray(val);

  // Recursive function to clean the object
  function clean(value: any) {
    if (value instanceof Date) return value;
    else if (isArray(value)) {
      // Recursively clean each item in the array
      const filteredArray = value.map(clean).filter((item) => item != null);
      return filteredArray.length ? filteredArray : null;
    } else if (isObject(value)) {
      // Recursively clean each property of the object
      const cleanedObject = Object.entries(value).reduce((acc, [key, val]) => {
        const cleanedVal = clean(val);
        if (cleanedVal !== null) acc[key] = cleanedVal;
        return acc;
      }, {});

      // Additional condition: if the object only has an "id" key, remove it
      const keys = Object.keys(cleanedObject);
      if (keys.length === 1 && keys[0] === 'id') return null;

      return keys.length ? cleanedObject : null;
    }
    // Return value only if it's not null, undefined, or an empty string
    return value != null && value != undefined && value !== '' ? value : null;
  }

  return clean(obj);
}
